import React, { useState, useEffect, useCallback } from 'react';
import './Carousel.css';

const ModernCarousel = ({ images, autoplaySpeed = 5000, showArrows = true, showDots = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Support both plain string sources and objects { src, position }.
  // `position` maps to CSS object-position (e.g. "center", "50% 30%", "top")
  // and lets each image fine-tune which part stays centered when cropped.
  const getSrc = (image) => (typeof image === 'string' ? image : image.src);
  const getPosition = (image) =>
    typeof image === 'string' ? 'center' : image.position || 'center';
  const getAlt = (image, index) =>
    typeof image === 'string' ? `Immagine ${index + 1}` : image.alt || `Immagine ${index + 1}`;

  // Navigation functions
  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Setup autoplay
  useEffect(() => {
    if (autoplaySpeed <= 0 || isPaused) return;
    
    const interval = setInterval(() => {
      goToNext();
    }, autoplaySpeed);
    
    return () => clearInterval(interval);
  }, [autoplaySpeed, goToNext, isPaused]);

  return (
    <div className="modern-carousel">
      <div 
        className="carousel-container"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Display current image */}
        <div className="carousel-slide image-container">
          <img
            src={getSrc(images[currentIndex])}
            alt={getAlt(images[currentIndex], currentIndex)}
            className="carousel-image"
            style={{ objectPosition: getPosition(images[currentIndex]) }}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            width="1600"
            height="900"
          />
          <div className="image-overlay"></div>
        </div>

        {/* Navigation arrows */}
        {showArrows && (
          <>
            <button 
              className="carousel-arrow carousel-arrow-left" 
              onClick={goToPrev}
              aria-label="Previous slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button 
              className="carousel-arrow carousel-arrow-right" 
              onClick={goToNext}
              aria-label="Next slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Dots indicators */}
      {showDots && (
        <div className="carousel-dots">
          {images.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ModernCarousel;
